import { useState } from 'react';
import apiClient from '../Services/AuthService';

interface GenerateTextRequest {
  original_text: string;
  number_distinction_type: 'SINGULAR' | 'PLURAL';
  distinction_type: 'INFORMAL' | 'FORMAL';
}

interface GenerateTextResponse {
  original_text: string;
  transformed_text: string;
  number_distinction: string;
  tv_distinction: string;
}

type VariationType = 'tuSingular' | 'tuPlural' | 'udSingular' | 'udPlural';

const useGenerateText = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapVariationToParams = (variation: VariationType) => {
    const mappings = {
      tuSingular: { number_distinction_type: 'SINGULAR' as const, distinction_type: 'INFORMAL' as const },
      tuPlural: { number_distinction_type: 'PLURAL' as const, distinction_type: 'INFORMAL' as const },
      udSingular: { number_distinction_type: 'SINGULAR' as const, distinction_type: 'FORMAL' as const },
      udPlural: { number_distinction_type: 'PLURAL' as const, distinction_type: 'FORMAL' as const },
    };
    return mappings[variation];
  };

  const generateText = async (originalText: string, variation: VariationType): Promise<string> => {
    if (!originalText || originalText.trim() === '') {
      throw new Error('El texto original no puede estar vacío');
    }

    setIsGenerating(true);
    setError(null);

    try {
      const params = mapVariationToParams(variation);
      
      const requestData: GenerateTextRequest = {
        original_text: originalText,
        ...params
      };

      const response = await apiClient.post<GenerateTextResponse>('/ai/generate-text', requestData);
      
      if (response.data && response.data.transformed_text) {
        return response.data.transformed_text;
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error: any) {
      let errorMessage = 'Error al generar el texto';
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.message === 'string') {
          errorMessage = data.message;
        } else if (typeof data.error === 'string') {
          errorMessage = data.error;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else if (error.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateText,
    isGenerating,
    error,
    clearError: () => setError(null)
  };
};

export default useGenerateText;